import { css, keyframes } from "styled-components";
import { textUppercaseMixin } from "../../../../skins/shared/mixins/typography";
const inRight = keyframes `
  0% {
    opacity: 0;
    transform: translateX(250px);
  }
  50% {
    opacity: 0;
    transform: translateX(125px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;
const inLeft = keyframes `
0% {
  opacity: 0;
  transform: translateX(-250px);
}

50% {
  opacity: 0;
  transform: translateX(-125px);
}

100% {
  opacity: 1;
  transform: translateX(0);
}`;
const mediaQueryMobileLayout = "@media screen and (max-width: 700px)";
export const lionSkin = (chroma, paletteTokens) => css `
  & {
    display: flex;
    flex-direction: column;
    position: relative;

    .character-select {
      /* asset dimensions should match the asset dimensions coming from the cms */
      --thumbnail-asset-width: 160;
      --thumbnail-asset-height: 196;

      /* thumbnail configuration. */
      --thumbnail-height: 196px; /* sets the height of the thumbnail row (including space for the active art popping out of fill) */
      --thumbnail-fill-offset: 8%; /* determines how much the art will pop out of the fill when active */
      --slant-width-ratio: 0.1763; /* based on 10deg skew.  Used to calculate width of thumbnail fill (since the skewed box will be narrower than the art). */

      /* calculated properties.  There should be no need to adjust these to tweek the design. */
      --thumbnail-asset-aspect-ratio: calc(var(--thumbnail-asset-width) / var(--thumbnail-asset-height));
      --slant-width: calc(var(--thumbnail-height) * var(--slant-width-ratio));
      --thumbnail-width: calc(
        0.95 * var(--thumbnail-height) * var(--thumbnail-asset-aspect-ratio) - var(--slant-width)
      );
      --thumbnail-slant-offset: calc(var(--slant-width) / 2);

      ${chroma.mediaQueries.tabletDown} {
        /* note that to make the thumbnails responsive, we only need to change the thumbnail height. */
        --thumbnail-height: 130px;
      }
      ${chroma.mediaQueries.mobileDown} {
        --thumbnail-height: calc(21vw / var(--thumbnail-asset-aspect-ratio)); /* fit 5 thumbnails on screen */
      }

      overflow: hidden;
      position: relative;
      width: 100%;
      flex-grow: 1;
      ${mediaQueryMobileLayout} {
        display: flex;
        flex-direction: column;
      }

      .top-section {
        display: flex;
        flex-direction: column;
        ${mediaQueryMobileLayout} {
          order: 1;
        }

        .banner-decoration {
          padding: 8px 0;
          margin-bottom: 8px;
          ${chroma.mediaQueries.mobileDown} {
            padding: 5px 0;
          }

          .hash {
            height: 30px;
            width: 100%;
            background: repeating-linear-gradient(
              45deg,
              ${paletteTokens.bladebg1},
              ${paletteTokens.bladebg1} 5px,
              transparent 5px,
              transparent 10px
            );
            ${chroma.mediaQueries.tabletDown} {
              height: 20px;
            }
            ${chroma.mediaQueries.mobileDown} {
              height: 14px;
            }
          }
        }

        .character-name-banner {
          padding: 10px 0;
          flex-grow: 1;
          height: 120px;
          ${chroma.mediaQueries.mobileDown} {
            height: 100px;
          }

          .character-name {
            ${chroma.typography.display2}
            height: 100%;
            ${textUppercaseMixin}
            line-height: 1;
            padding: 0 ${chroma.spacers.variable.l};
            font-weight: bold;
            width: 50%;
            animation: ${inLeft} 400ms ease 0s 1 normal forwards;
            color: ${paletteTokens.underlay};

            > div {
              height: 100%;

              & > * {
                display: flex !important;
                align-items: center;
                height: 100%;
                color: ${paletteTokens.bladetext1};

                &:lang(ko-kr) {
                  position: relative;
                  top: 9px;
                }
              }
            }
            ${mediaQueryMobileLayout} {
              width: 100%;
            }
          }
        }
      }

      .character-details {
        width: 50%;
        padding: 0 ${chroma.spacers.variable.l};
        min-height: 232px; /* minimize layout shift when switching champions (2 lines of tagline + 5 lines of description) */

        ${mediaQueryMobileLayout} {
          width: 100%;
          order: 4;
          min-height: auto;
        }

        .character-tagline {
          color: ${paletteTokens.bladetext1};
          ${textUppercaseMixin}
          ${chroma.typography.display6}
          margin: ${chroma.spacers.variable.m} 0;
        }

        .character-description {
          ${chroma.typography.body2}
          margin: ${chroma.spacers.variable.m} 0;
          ${mediaQueryMobileLayout} {
            margin-bottom: ${chroma.spacers.variable.l};
          }
        }
      }

      .bottom-bar {
        height: 15px;
        width: 100%;
        ${mediaQueryMobileLayout} {
          display: none;
        }
      }

      .character-thumbnail-list {
        position: relative;
        z-index: 9;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        overflow: hidden;
        margin: ${chroma.spacers.variable.m} ${chroma.spacers.variable.l};
        margin-right: 0; /* allow thumbnails to extend to the edge of the screen disappearing */
        margin-bottom: ${chroma.spacers.variable.l};
        height: var(--thumbnail-height);
        padding: 2px 0;
        padding-left: var(--thumbnail-slant-offset); /* account for skew space */

        ${chroma.mediaQueries.tabletDown} {
          gap: 6px;
        }
        ${mediaQueryMobileLayout} {
          order: 3;
          flex-wrap: nowrap;
          position: relative;
          z-index: 2;
          overflow-x: scroll;
          -ms-overflow-style: none;
          scrollbar-width: none;
          margin-top: 0; /* remove vertical margins to keep featured art aligned to bottom of thumbnails */
          margin-bottom: 0;

          &::-webkit-scrollbar {
            display: none;
          }
        }

        .character-thumbnail {
          flex-shrink: 0;
          transform: skew(-10deg);
          height: var(--thumbnail-height);
          width: var(--thumbnail-width);
          position: relative;
          overflow: hidden;

          .fill {
            position: absolute;
            top: var(--thumbnail-fill-offset);
            background-color: ${paletteTokens.bladebg2};
            opacity: 0.4;
            bottom: 0;
            right: 0;
            left: 0;
          }

          .art {
            transform: skew(10deg);
            position: absolute;
            aspect-ratio: var(--thumbnail-asset-aspect-ratio);
            top: var(--thumbnail-fill-offset); /* art is contained within fill */
            bottom: 0;
            left: calc(var(--thumbnail-slant-offset) * -1);
            transition: top ease-in-out 0.1s;

            img {
              object-fit: contain;
              max-height: 100%;
              width: 100%;
            }
          }

          &.selected {
            .fill {
              background-color: ${paletteTokens.bladebg2};
              opacity: 1;
            }

            .art {
              top: 0; /* art pops out the top of the fill */
            }
          }

          &:focus {
            outline: none;

            &.selected {
              .fill {
                background-color: ${paletteTokens.bladebg2};
                opacity: 1;
              }
            }
          }

          &:not(.disabled) {
            cursor: pointer;
          }

          &:not(.disabled):hover,
          &:focus:not(:focus-visible) {
            .fill {
              background-color: ${paletteTokens.bladebg2};
              opacity: 1;
            }
          }
        }
      }

      .featured-image-wrapper {
        pointer-events: none;
        position: absolute;
        top: 0;
        right: 0;
        width: 50%;
        height: 100%;
        overflow: hidden;
        z-index: 10;
        animation: ${inRight} 400ms ease 0s 1 normal forwards;

        ${mediaQueryMobileLayout} {
          z-index: 1;
          position: static;
          width: 100%;
          height: clamp(400px, 100vw, 600px);
          overflow: hidden;
          order: 2;
          margin-bottom: calc(var(--thumbnail-height) * -1 + 1px);
        }

        .featured-image {
          object-fit: cover;
          width: 100%;
          height: auto;
          object-position: top;
        }
      }

      .backdrop {
        pointer-events: none;
        position: absolute;
        bottom: 0;
        top: 0;
        right: 0;
        width: 50%;
        height: 100%;
        ${mediaQueryMobileLayout} {
          display: none;
        }

        & > svg {
          height: inherit;
          width: 100%;
        }
      }
    }

    .title-bar {
      margin: 14px 0 8px;
      display: flex;
      gap: 14px;
      overflow: hidden;
      padding-left: ${chroma.spacers.variable.l};

      h2 {
        ${textUppercaseMixin}
        ${chroma.typography.display5}
      }
      ${chroma.mediaQueries.mobileDown} {
        gap: 10px;
      }

      .title-decoration {
        flex-grow: 1;
        display: flex;

        .accent {
          height: 100%;
          margin-right: -2px;

          & > svg {
            height: inherit;
            width: inherit;

            & > path {
              height: 100%;
              width: 100%;
            }
          }
        }

        .accent-fill {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
`;
//# sourceMappingURL=index.js.map